import { jsPDF } from "jspdf";
import moment from "moment-timezone";
import QRCode from "qrcode";
import React from "react";
import { languages } from "../../constants/languages";
import { groupBy } from "../../util/GroupBy";

export const exportTeamQRCodes = async (tournament, divisionsObject) => {
  const generateQRCode = async (url) => {
    try {
      const qrDataUrl = await QRCode.toDataURL(url, {
        margin: 1,
        width: 200,
      });
      return qrDataUrl;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  const truncateTeamName = (name) => {
    return name.length > 30 ? name.substring(0, 30) + "..." : name;
  };

  const sortTeamsAlphabetically = (teams) => {
    return teams.sort((a, b) => {
      function chunkify(t) {
        var tz = [],
          x = 0,
          y = -1,
          n = 0,
          i,
          j;

        while ((i = (j = t.charAt(x++)).charCodeAt(0))) {
          var m = i === 46 || (i >= 48 && i <= 57);
          if (m !== n) {
            tz[++y] = "";
            n = m;
          }
          tz[y] += j;
        }
        return tz;
      }

      let toChunkA = a.name || "";
      let toChunkB = b.name || "";

      var aa = chunkify(toChunkA.toString().toLowerCase());
      var bb = chunkify(toChunkB.toString().toLowerCase());

      for (let x = 0; aa[x] && bb[x]; x++) {
        if (aa[x] !== bb[x]) {
          var c = Number(aa[x]),
            d = Number(bb[x]);
          if (c === aa[x] && d === bb[x]) {
            return c - d;
          } else return aa[x] > bb[x] ? 1 : -1;
        }
      }
      return aa.length - bb.length;
    });
  };

  const addPageHeader = (
    doc,
    pageNum,
    totalPages,
    pageWidth,
    marginVertical,
    divisionName = null
  ) => {
    // Add title with tournament name and optionally division name
    doc.setFontSize(14);
    let titleText = tournament.name;
    if (divisionName) {
      titleText += " — " + divisionName;
    }
    titleText += ` (${pageNum}/${totalPages})`;
    const titleWidth =
      (doc.getStringUnitWidth(titleText) * 14) / doc.internal.scaleFactor;
    const titleX = (pageWidth - titleWidth) / 2;
    doc.text(titleText, titleX, marginVertical + 10);
  };

  try {
    // Get all teams first
    const teamsSnapshot = await window.firebase
      .firestore()
      .collection("tournaments")
      .doc(tournament[".key"])
      .collection("teams")
      .get();

    const teams = [];
    teamsSnapshot.forEach((doc) => {
      let team = doc.data();
      team["id"] = doc.id;
      if (team.createdAt) {
        team["added"] = moment(team.createdAt.toDate()).unix();
      }
      teams.push(team);
    });

    // Get all team codes
    const codesSnapshot = await window.firebase
      .firestore()
      .collection("tournaments")
      .doc(tournament[".key"])
      .collection("teamCodes")
      .where("type", "==", "team")
      .get();

    const codes = {};
    codesSnapshot.forEach((doc) => {
      codes[doc.data().teamId] = doc.id;
    });

    // Generate PDF
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    const marginHorizontal = 40; // Left and right margins
    const marginVertical = 15; // Top and bottom margins
    const qrSize = 45;
    const qrPerRow = 2;
    const qrSpacing =
      (pageWidth - 2 * marginHorizontal - qrSize * qrPerRow) / (qrPerRow - 1);
    const qrWithLabelHeight = qrSize + 15;
    const qrPerPage = 8;

    // Group teams by division and sort teams within each division
    const teamsPerDivision = groupBy(teams, "division");
    Object.keys(teamsPerDivision).forEach((key) => {
      teamsPerDivision[key] = sortTeamsAlphabetically(teamsPerDivision[key]);
    });

    let isFirstDivision = true;

    // Process each division
    for (const divisionId of Object.keys(teamsPerDivision).sort(
      (aKey, bKey) => {
        let a = divisionsObject[aKey] || {};
        let b = divisionsObject[bKey] || {};
        if (a.num > b.num) return 1;
        if (a.num < b.num) return -1;
        if (a.id > b.id) return 1;
        if (a.id < b.id) return -1;
        return 0;
      }
    )) {
      const division = divisionsObject[divisionId] || {};
      const teamsInDivision = teamsPerDivision[divisionId].filter(
        (team) => codes[team.id]
      );

      // Calculate total pages for this division
      const totalPagesForDivision = Math.ceil(
        teamsInDivision.length / qrPerPage
      );
      let divisionPageCount = 1;

      // Add new page for each division (except first)
      if (!isFirstDivision) {
        doc.addPage();
      }
      isFirstDivision = false;

      // Reset positions for new division
      let currentY = marginVertical + 25;
      let currentX = marginHorizontal;
      let count = 0;

      // Add header for this division
      addPageHeader(
        doc,
        divisionPageCount,
        totalPagesForDivision,
        pageWidth,
        marginVertical,
        division.name
      );

      // Process teams in this division
      for (const team of teamsInDivision) {
        if (codes[team.id]) {
          const loginUrl =
            "https://" +
            (tournament.isPSD || tournament.crqpsd
              ? "tournify.prosoccerdata.com/"
              : "tournifyapp.com/live/") +
            tournament.liveLink +
            "/team/" +
            team.id +
            "/" +
            codes[team.id];

          const qrDataUrl = await generateQRCode(loginUrl);

          if (qrDataUrl) {
            // Add new page if needed within same division
            if (count > 0 && count % qrPerPage === 0) {
              doc.addPage();
              divisionPageCount++;
              currentX = marginHorizontal;
              currentY = marginVertical + 25;
              addPageHeader(
                doc,
                divisionPageCount,
                totalPagesForDivision,
                pageWidth,
                marginVertical,
                division.name
              );
            }

            // Add QR code
            doc.addImage(qrDataUrl, "PNG", currentX, currentY, qrSize, qrSize);

            // Add team name below QR code
            doc.setFontSize(10);
            const truncatedName = truncateTeamName(team.name);
            const textWidth =
              (doc.getStringUnitWidth(truncatedName) * 10) /
              doc.internal.scaleFactor;
            const textX = currentX + (qrSize - textWidth) / 2;
            doc.text(truncatedName, textX, currentY + qrSize + 4);

            // Update position
            if (count % qrPerRow === qrPerRow - 1) {
              currentX = marginHorizontal;
              currentY += qrWithLabelHeight;
            } else {
              currentX += qrSize + qrSpacing;
            }

            count++;
          }
        }
      }
    }

    // Save the PDF
    doc.save(
      languages.teams.QRcodes +
        " " +
        (tournament.individualPlayers
          ? languages.teams.playersLow
          : languages.tournament.teamsLow) +
        " - " +
        tournament.name +
        ".pdf"
    );
  } catch (error) {
    console.error("Error generating PDF:", error);
  }
};

// Wrapper component for backward compatibility
const ExportTeamQRCodesContainer = ({
  tournament,
  divisionsObject,
  autoExport,
}) => {
  React.useEffect(() => {
    if (autoExport) {
      exportTeamQRCodes(tournament, divisionsObject);
    }
  }, [autoExport, tournament, divisionsObject]);

  return null;
};

export default ExportTeamQRCodesContainer;
